import { Money, Round } from "bigint-money";
import type { TRangeCell } from "../tableUtils";
// Type-only import: keep this low-level util free of the data/react module graph.
import type { RangeSummaryCalc } from "../data";

export type TNumberOrDecimal = number | Money | string;

export function isNumber(str: string): number | null {
  const val = Number.parseFloat(str);
  return Number.isNaN(val) ? null : val;
}

const createFormatter = (withSeparator: boolean) => (decimalPlaces: number) =>
  new Intl.NumberFormat("en", {
    useGrouping: withSeparator,
    currency: "USD",
    maximumFractionDigits: decimalPlaces,
    minimumFractionDigits: decimalPlaces,
    // banker's rounding, rounding to even numbers on .5 but away from 0
    // must match the rounding strategy for the Money lib below
    roundingMode: "halfExpand",
    // never show the negative sign for -0
    signDisplay: "negative",
  });

const roundingStrategy = Round.HALF_AWAY_FROM_0;

type DecimalReturnType<T> = T extends Money ? Money : Money | null;

function bigintPower(base: bigint, exponent: bigint) {
  if (exponent < 0n) {
    return { numerator: 1n, denominator: base ** -exponent };
  }
  return { numerator: base ** exponent, denominator: 1n };
}

export function Decimal<T extends number | string | Money>(n: T): DecimalReturnType<T> {
  if (n instanceof Money) return n as DecimalReturnType<T>;

  const currency = "USD";

  // Helper function to create Money object
  const createMoney = (value: string | number | bigint) => {
    return new Money(value.toString(), currency, roundingStrategy);
  };

  try {
    // Attempt to create Money object directly
    return createMoney(n) as DecimalReturnType<T>;
  } catch (e1) {
    // If the initial conversion fails, check if the input is a number in exponential notation
    if (typeof n === "number" && n.toString().includes("e")) {
      try {
        // Determine if the number is negative
        const isNegative = n < 0;
        // Convert the absolute value of the number to a string in exponential notation
        const exponentialString = Math.abs(n).toExponential();
        // Split the string into base and exponent
        const [base, exponent] = exponentialString.split("e");
        // Convert the base to a BigInt by scaling it up
        const [integerPart, fractionalPart] = base.split(".");
        const significantDigits = (fractionalPart || "").length;
        const scaledBase = BigInt(integerPart + (fractionalPart || ""));
        const scaleFactor = bigintPower(BigInt(10), BigInt(Number.parseInt(exponent, 10) - significantDigits));
        const baseBigInt = (scaledBase * scaleFactor.numerator) / scaleFactor.denominator;
        const result = createMoney(baseBigInt);
        const finalResult = isNegative ? result.multiply(-1) : result;
        return finalResult as DecimalReturnType<T>;
      } catch (e2) {
        console.error("Error creating decimal object after handling exponential notation", { n, e2 });
        return null as DecimalReturnType<T>;
      }
    } else {
      console.error("Error creating decimal object", { n, e1 });
      return null as DecimalReturnType<T>;
    }
  }
}

export type TNumber = number;
export type TMoney = Money;

function toNumber(n: Money): number {
  return Number.parseFloat(n.format());
}

const parseNumberValidationRegex = /^-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?$/;
function parseNumber(n: unknown): number | null {
  if (typeof n === "number") return n;
  if (typeof n === "string") {
    if (parseNumberValidationRegex.test(n)) {
      const parsed = Number.parseFloat(n);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }
  if (n instanceof Money) return toNumber(n);
  return null;
}

/**
 * This is a formatter for life!
 * Reuse it often, don't just use it once then throw it away - it's expensive to produce:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
 *
 * Is equivalent to the Money class's toFixed method
 */
export const createFormatterWithoutSeparator = createFormatter(false);

/**
 * This is a formatter for life!
 * Reuse it often, don't just use it once then throw it away - it's expensive to produce:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
 *
 * Is equivalent to the Money class's toFixed method
 */
export const createFormatterWithSeparator = createFormatter(true);

type BoundedFormatterDecimalPlaces = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export const minimumDecimalPlaces = 0;
// max, inclusive
export const maximumDecimalPlaces = 9;
// TODO: ^ this is currently determined by market-grid/modals/Formatting/FormatSettings.tsx
// probably should be clarified as a constant, perhaps imported from here?

export const boundedFormatterDecimalPlaces = (num: number): BoundedFormatterDecimalPlaces =>
  Math.min(Math.max(num | 0, minimumDecimalPlaces), maximumDecimalPlaces) as BoundedFormatterDecimalPlaces;

const cachedFormatters: Array<Intl.NumberFormat> = Array.from({
  length: maximumDecimalPlaces,
});

export const inFormatterDecimalPlacesBounds = (
  num: BoundedFormatterDecimalPlaces,
): num is BoundedFormatterDecimalPlaces =>
  num >= minimumDecimalPlaces && num <= maximumDecimalPlaces && num === (num | 0);

// There's some duplication here but I want it to be pretty fast
// and possibly inlineable for the fast case
export const getBoundedFormatterWithoutSeparator = (decimalPlaces: BoundedFormatterDecimalPlaces) => {
  let formatter = cachedFormatters[decimalPlaces];
  if (formatter === undefined) {
    formatter = createFormatterWithoutSeparator(decimalPlaces);
    cachedFormatters[decimalPlaces] = formatter;
  }
  return formatter;
};

/**
 * Decimal places will be bound to {@link BoundedFormatterDecimalPlaces}
 */
export const getFormatterWithoutSeparator = (decimalPlaces: number) =>
  getBoundedFormatterWithoutSeparator(boundedFormatterDecimalPlaces(decimalPlaces));

const cachedFormattersWithSeparators: Array<Intl.NumberFormat> = Array.from({
  length: maximumDecimalPlaces,
});

// There's some duplication here but I want it to be pretty fast
// and possibly inlineable for the fast case
export const getBoundedFormatterWithSeparator = (decimalPlaces: BoundedFormatterDecimalPlaces) => {
  let formatter = cachedFormattersWithSeparators[decimalPlaces];
  if (formatter === undefined) {
    formatter = createFormatterWithSeparator(decimalPlaces);
    cachedFormattersWithSeparators[decimalPlaces] = formatter;
  }
  return formatter;
};

/**
 * Decimal places will be bound to {@link BoundedFormatterDecimalPlaces}
 */
export const getFormatterWithSeparator = (decimalPlaces: number) =>
  getBoundedFormatterWithSeparator(boundedFormatterDecimalPlaces(decimalPlaces));

function safeSum(numbers: (TNumber | string)[]): Money | null {
  try {
    const init = Decimal(0);
    if (!init) return null;
    const sum = numbers.reduce((acc: Money, n) => {
      if (typeof n === "string") {
        const parsed = isNumber(n);
        if (parsed === null) return acc;
        const decimal = Decimal(parsed);
        if (!decimal) return acc;
        return acc.add(decimal);
      }
      const decimal = Decimal(n);
      if (!decimal) return acc;
      return acc.add(decimal);
    }, init);
    return sum;
  } catch (e) {
    console.error(e);
    return null;
  }
}

function mean(nums: (TNumber | string)[]): Money | undefined | null {
  if (nums.length === 0) return null;
  try {
    const meanResult = safeSum(nums)?.divide(nums.length);
    return meanResult;
  } catch (e) {
    console.error("error creating mean", { nums, e });
    return null;
  }
}

export type RangeCellWithValue = TRangeCell & { cellValue: number };

function selectionToValues(rangeSelection: RangeCellWithValue[]) {
  return rangeSelection.map((cell) => cell.cellValue);
}

const defaultDiffRatio = { diff: 0, ratio: 0 };

// Fallback decimal places for aggregation summaries when a caller doesn't pass
// one. Mirrors DEFAULT_RANGE_SUMMARY_DP in data/grid-settings (kept as a local
// literal so this low-level util avoids importing the data/react module graph).
const DEFAULT_AGG_DECIMAL_PLACES = 2;

// TODO: what do these regexes do? document and explain them and extract them from this function for clarity and performance
// Formats to `up to` decimalPlaces (trailing zeros are trimmed).
export function formatAgg(value: Money | null, decimalPlaces: number = DEFAULT_AGG_DECIMAL_PLACES) {
  if (!value) return null;
  return value
    .toFixed(decimalPlaces)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0*$/, "");
}

function genSumAndCount(
  rangeSelection: RangeCellWithValue[][],
  diffRatioCalc: RangeSummaryCalc = "average",
  // Diff/Ratio/Average/Sum are shown to up to this many decimal places. Count is always 0dp.
  decimalPlaces: number = DEFAULT_AGG_DECIMAL_PLACES,
) {
  const numbers = selectionToValues(rangeSelection.flat());
  const count = numbers.length;
  if (!count || count === 1) return null;
  const sum = safeSum(numbers);
  const zeroSum = !sum || sum?.isEqual(0);
  const avg = zeroSum ? 0 : formatAgg(sum.divide(count), decimalPlaces);
  const scm = { sum: formatAgg(sum, decimalPlaces), count: count.toString(), avg };
  if (rangeSelection.length !== 2) {
    return scm;
  }

  const scmdr = { ...scm, diffRatio: defaultDiffRatio };

  if (!count) return scmdr;

  // Diff and Ratio are derived from an aggregate of each range. Users can choose whether that aggregate is the average (default) or the sum.
  const aggregate = diffRatioCalc === "sum" ? safeSum : mean;
  const firstRange = selectionToValues(rangeSelection[0]);
  const secondRange = selectionToValues(rangeSelection[1]);
  const firstSum = aggregate(firstRange);
  const secondSum = aggregate(secondRange);
  const zeroFirst = !firstSum || firstSum?.isEqual(0);
  const zeroSecond = !secondSum || secondSum?.isEqual(0);

  if (!firstSum || !secondSum) return scmdr;

  const diff = formatAgg(firstSum.subtract(secondSum), decimalPlaces);
  const ratio = !zeroFirst && !zeroSecond ? formatAgg(firstSum.divide(secondSum), decimalPlaces) : 0;
  return { ...scm, diffRatio: { diff, ratio } };
}

export { parseNumber, safeSum, mean, genSumAndCount, selectionToValues };
